import type presentationMessages from '../../i18n/locales/en/presentation.json'
import type { TranslateFn } from './TranslateFn'

type FieldMessageKey = `presentation.errors.field.${Extract<
  keyof typeof presentationMessages.presentation.errors.field,
  string
>}`

/** Display keys of the custom issue codes the request contracts raise client-side. */
const keyByCode = {
  address_required: 'presentation.errors.field.addressRequired',
  address_unresolved: 'presentation.errors.field.addressUnresolved',
  city_required: 'presentation.errors.field.cityRequired',
  close_time_required: 'presentation.errors.field.closeTimeRequired',
  code_required: 'presentation.errors.field.codeRequired',
  'details-must-not-be-empty': 'presentation.errors.field.orderModificationDetailsEmpty',
  'details-required-without-items': 'presentation.errors.field.orderModificationChangeRequired',
  delivery_radius_required: 'presentation.errors.field.deliveryRadiusRequired',
  event_end_before_event_date: 'presentation.errors.field.eventEndBeforeEventDate',
  'items-required-without-details': 'presentation.errors.field.orderModificationChangeRequired',
  latitude_required: 'presentation.errors.field.addressLocationRequired',
  linked_items_duplicate: 'presentation.errors.field.linkedItemsDuplicate',
  longitude_required: 'presentation.errors.field.addressLocationRequired',
  multiple_days_notice_invalid: 'presentation.errors.field.multipleDaysNoticeInvalid',
  open_time_required: 'presentation.errors.field.openTimeRequired',
  password_confirmation_mismatch: 'presentation.errors.field.passwordConfirmationMismatch',
  placements_menu_category_id_duplicate: 'presentation.errors.field.placementsDuplicate',
  requested_date_required_with_requested_time: 'presentation.errors.field.requestedDateRequired',
  requested_time_required_with_requested_date: 'presentation.errors.field.requestedTimeRequired',
  state_required: 'presentation.errors.field.stateRequired',
  temporary_closures_ends_before_starts:
    'presentation.errors.field.temporaryClosureEndsBeforeStarts',
  temporary_closures_ends_in_past: 'presentation.errors.field.temporaryClosureEndsInPast',
  temporary_closures_overlap: 'presentation.errors.field.temporaryClosuresOverlap',
  temporary_closures_ownership_invalid:
    'presentation.errors.field.temporaryClosuresOwnershipInvalid',
  time_invalid: 'presentation.errors.field.timeInvalid',
  zip_required: 'presentation.errors.field.zipRequired'
} as const satisfies Readonly<Record<string, FieldMessageKey>>

type ClientIssueCode = keyof typeof keyByCode

const isClientIssueCode = (message: string): message is ClientIssueCode =>
  Object.hasOwn(keyByCode, message)

/** A client issue's display text: a contract code resolves to its localized copy; zod's own messages arrive localized by the zod error map. */
export function resolveClientIssueMessage(message: string, t: TranslateFn): string {
  return isClientIssueCode(message) ? t(keyByCode[message]) : message
}
